/**
 * useShellDomainMap - the map's live answers: cover, range and arc between
 * the actor and the target, the weapon's own gates, the ground folded into
 * every tab, who an area attack catches, pings, auras, the template preview
 * and the range band the attack rolls at.
 *
 * A domain hook: it reads what it needs from the shell scope `s` and returns
 * what it declares (see DeathWatchSimulator). Hook order unchanged.
 * Extracted from DeathWatchSimulator.
 */
import { useRef } from 'react';
import useShellWeaponGates from './useShellWeaponGates';
import { useShellLiveMap, useShellLiveTabs } from './useShellLiveMap';
import { resolveRangeBand } from '../engine/attackCalc';
import useShellAreaTargeting from './useShellAreaTargeting';
import useShellAreaByHand from './useShellAreaByHand';
import useShellPings from './useShellPings';
import useShellAuras from './useShellAuras';
import useShellAreaPreview from './useShellAreaPreview';
import useSituationalEffects from './useSituationalEffects';

/**
 * The live map, the weapon's gates, the tabs with the ground folded in and
 * the measure line. Length: the hooks' argument records (declarative).
 */
function useShellLiveGround(s) {
  const {
    selectedSlot,
    roster,
    actorId,
    rosterTabs,
    activeTab,
    isGm,
    currentSlots,
    effectiveSlot,
    attackMod,
    terrain,
    ruleTokens,
    sync,
    coverConflictMode,
    battlefieldDerivedValues,
    autoApplyMapModifiers,
    surfacesModifyCombatants,
    vehicleArcPreset,
    relationRules,
  } = s;
  // The map's live answer between this client's actor and the active
  // target - cover, range band, facing, arc. See `hooks/useShellLiveMap`.
  const { mapSolids, liveMap } = useShellLiveMap({
    terrain,
    rosterTabs,
    actorId,
    activeTab,
    tokens: ruleTokens,
    weaponRange: currentSlots[selectedSlot]?.range,
    isGm,
    enabled: battlefieldDerivedValues && autoApplyMapModifiers,
    arcPreset: vehicleArcPreset,
    relationRules,
    roster,
    hiddenIds: s.hidden?.hiddenIds,
  });

  /**
   * The band the attack actually rolls at.
   *
   * Auto falls back to Normal when the map cannot answer - nobody on the
   * map, no weapon range, out of Extreme - which is exactly what the
   * control used to default to, so a session with no battlefield behaves
   * as it always did.
   */
  const rangeBand = resolveRangeBand(s.rangeBandChoice, liveMap.rangeBand);
  // The acting character's conditional effects, applied now that the target, the scene AND the map are known
  // (plan armoury: a condition may ask the range, the distance or the cover). REPLACES `effectiveCustomMods`,
  // `attackMod` and `attackerCharStats` when it adds any; the gates below read its `attackMod`.
  const situated = useSituationalEffects({ ...s, liveMap, rangeBand });

  // What the armed weapon's own rules say about the next shot - Indirect's
  // blind arc and minimum range, Cycling's recharge. See the hook.
  const { indirectFire, attackBlockedReason, meleeReachReason, attackModFinal, startCooldown } = useShellWeaponGates({
    // A holding weapon rule may grant a special that gates the shot (Indirect, Cycling): the ruled slot.
    effectiveSlot: situated.effectiveSlot ?? effectiveSlot,
    selectedSlot,
    measurement: liveMap.measurement,
    reachGap: liveMap.reachGap,
    // Rounds, and the beats of time passed outside them: either recharges a capacitor.
    trackerRound: (sync.turnTrackerState?.round || 0) + (s.direction?.frame?.beats ?? 0),
    attackMod: situated.attackMod ?? attackMod,
    actorConds: s.actor?.state?.conditions,
    fireMode: s.fireMode,
  });

  // What the ground does to everyone, and the roster's tabs with the live
  // map folded in - derived, never stored. See `hooks/useShellLiveMap`.
  const { surfaceEffects, surfaceEffectsRef, tabs } = useShellLiveTabs({
    roster,
    rosterTabs,
    tokens: ruleTokens,
    terrain,
    activeTab,
    liveMap,
    coverConflictMode,
    surfacesOn: surfacesModifyCombatants,
  });

  /**
   * A MEASURE LINE, WITHOUT RE-RENDERING THE WORLD FOR EVERY MOUSEMOVE.
   *
   * The blast template is drawn where the tape ends, so the attack has to
   * know where that is. Lifting the line into state here would re-render the
   * whole application on every pixel of a drag; a ref costs nothing and is
   * read exactly once, at the moment somebody fires.
   */
  const measureLineRef = useRef(null);
  return {
    ...situated,
    rangeBand,
    mapSolids,
    liveMap,
    indirectFire,
    attackBlockedReason,
    meleeReachReason,
    attackModFinal,
    startCooldown,
    surfaceEffects,
    surfaceEffectsRef,
    tabs,
    measureLineRef,
  };
}

/**
 * Area targeting, pings, auras, the template preview and the range band.
 * Length: the hooks' argument records (declarative).
 */
function useShellMapReach(s) {
  const {
    selectedSlot,
    roster,
    actorId,
    actor,
    rosterTabs,
    activeTab,
    isGm,
    sessionId,
    playerId,
    displayName,
    mergePingsRef,
    addNarrativeRef,
    flushNarrativeQueue,
    currentSlots,
    attackMode,
    terrain,
    tokens,
    ruleTokens,
    positionsCount,
    coverConflictMode,
    relationRules,
    ignoreTerrain,
    blastSelfPolicy,
    battlefieldDerivedValues,
    autoApplyMapModifiers,
    vehicleArcPreset,
    mapSolids,
    surfaceEffectsRef,
    tabs,
    measureLineRef,
  } = s;

  // WHO A BLAST (OR A CONE, A BEAM, AN ARC, A KILL ZONE) CATCHES - the map's
  // answer, handed to the attack. See `hooks/useShellAreaTargeting`.
  const mapAnswers = useShellAreaTargeting({
    tokens: ruleTokens,
    roster,
    actor,
    actorId,
    ignoreTerrain,
    mapSolids,
    isGm,
    arcPreset: vehicleArcPreset,
    derived: battlefieldDerivedValues && autoApplyMapModifiers,
    coverConflictMode,
    relationRules,
    blastSelfPolicy,
    attackMode,
    surfaceEffectsRef,
    measureLineRef,
  });
  // ...and the same three answers from a tick list, for while positions do
  // not count. One or the other is handed on; the attack cannot tell which.
  const handAnswers = useShellAreaByHand({ roster, actorId, coverConflictMode, attackMode, surfaceEffectsRef });
  const { suppressionZone, resolveVictims, resolveSpawned } = positionsCount ? mapAnswers : handAnswers;

  // Pings and climbs: speech aimed at the map, and the log lines that
  // point back at it. See `hooks/useShellPings`.
  const { pings, mapFocus, lookAtOnMap, handlePing, handleMapClimb, handleMapMove } = useShellPings({
    mapSolids,
    terrain,
    isGm,
    actorId,
    tokens,
    weaponRange: currentSlots[selectedSlot]?.range,
    playerId,
    displayName,
    sessionId,
    activeTab,
    addNarrativeRef,
    flushNarrativeQueue,
    mergePingsRef,
  });

  // Auras for the initiative roll, and the actor as the map finds them.
  const { initiativeModFor, actorProjection, actorTarget } = useShellAuras({
    rosterTabs,
    tabs,
    tokens: ruleTokens,
    actor,
  });

  // The selected weapon's template for the map (v8.372.5).
  const { previewArea, previewEligible, previewSpawnEligible, previewSizeHint } = useShellAreaPreview({
    slot: currentSlots[selectedSlot],
    actor,
    activeTab,
    roster,
  });

  return {
    suppressionZone,
    resolveVictims,
    resolveSpawned,
    areaTicksRef: handAnswers.ticksRef,
    pings,
    mapFocus,
    lookAtOnMap,
    handlePing,
    handleMapClimb,
    handleMapMove,
    initiativeModFor,
    actorProjection,
    actorTarget,
    // Frenzied is the ACTOR'S OWN state (v9.5.0): one flag on the combatant gives the +10 WS and blocks
    // Dodge and Parry. The cockpit switch that gave the bonus alone is gone.
    frenzied: !!actorTarget?.frenzied,
    previewArea,
    previewEligible,
    previewSpawnEligible,
    previewSizeHint,
  };
}

/**
 * @param {object} s - the shell scope (state, attacker, chrome and sync names).
 * @returns {object} the live map and derived tabs, the area and ping verbs,
 *   the actor's projection and `rangeBand`.
 */
export default function useShellDomainMap(s) {
  const liveGround = useShellLiveGround(s);
  const mapReach = useShellMapReach({ ...s, ...liveGround });
  return { ...liveGround, ...mapReach };
}
